import {
  IContentBlockTemplates,
  IContentBlocks, IRentsyncSettings,
} from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, convertQueryCommandToQueryString } from '@llws/dynamic-router-utils';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { DataModifyProcessor } from '../classes/data-modify-processor';
import config from '../../config';

const prefix = '/syndication-api/rentsync_settings';
const contentBlocksPrefix = '/syndication-api/content_blocks';

const contentBlocksUrl = `${config.adminGateway}/syndication-api/content_blocks`;

/**
 * Fetches Syndication Settings <br>
 * API Endpoint - '/syndication-api/rentsync_settings' via {@link adminRequest.get}
 * @param {IQueryCommandOptions} [filters] - optional query string filters
 */
export const get = (filters?: IQueryCommandOptions) => {
  const qs = convertQueryCommandToQueryString({
    ...filters || {},
    // ...{ select: ['$(count:buildings.id|status~not:deleted)'] },
  });
  return adminRequest.get(`${prefix}${qs}`);
};
/**
 * Fetches Syndication Settings by ID <br>
 * API Endpoint - '/syndication-api/rentsync_settings/${rensyncSettingId}' via {@link adminRequest.get}
 * @param rensyncSettingId - id of the syndication settings
 */
export const getById = (rensyncSettingId: number) => {
  const qs = convertQueryCommandToQueryString({
    // relations: [
    //   { path: 'buildings', alias: 'b' },
    // ],
  });

  return adminRequest.get(`${prefix}/${rensyncSettingId}${qs}`);
};

/**
 * Inserts Syndication Settings <br>
 * API Endpoint - '/syndication-api/rentsync_settings' via {@link adminRequest.post}
 * @param {IRentsyncSettings} data - settings to insert
 */
export const insert = (data: IRentsyncSettings) => adminRequest.post(`${prefix}`, data);

/**
 * Updates RentSync Settings <br>
 * API Endpoint - '/syndication-api/rentsync_settings/${rensyncSettingId}' via {@link adminRequest.put}
 * @param {number} rentsyncSettingId - id of the settings to update
 * @param {IRentsyncSettings} data - updated settings object
 */
export const update = (
  rentsyncSettingId: number,
  data: IRentsyncSettings,
) => adminRequest.put(`${prefix}/${rentsyncSettingId}`, data);

/**
 * Updates Content Blocks of the specified settings <br>
 * API Endpoint - '/syndication-api/rentsync_settings/${rensyncSettingId}/content_blocks' via {@link adminRequest.post}
 * @param {number} rentsyncSettingId - id of the settings to update
 * @param {IContentBlocks[]} data - updated content blocks
 */
export const updateContentBlocks = (
  rentsyncSettingId: number,
  data: IContentBlocks[],
) => adminRequest.post(`${contentBlocksPrefix}`, data);

/**
 * Deletes Rentsync Settings <br>
 * API Endpoint - '/syndication-api/rentsync_settings' via {@link adminRequest.delete}
 * @param {number} rentsyncSettingId - id of the settings to delete
 */
export const remove = (
  rentsyncSettingId: number,
) => adminRequest.delete(`${prefix}`, [rentsyncSettingId]);

/**
 * Deletes Content Blocks <br>
 * API Endppoint - '/syndication-api/content_blocks' via {@link adminRequest.delete}
 * @param {number[]} contentBlockIds - content block ids to delete
 */
export const removeContentBlocks = (
  contentBlockIds: number[],
) => adminRequest.delete(`${contentBlocksPrefix}`, contentBlockIds);

/**
 * Fetches content block templates
 * API Endppoint - `/syndication-api/content_block_templates${filters}` <br> via {@link adminRequest.get}
 */
export const getContentBlockTemplates = () => {
  const filters: IQueryCommandOptions = {
    // relations: [
    //   {
    //     alias: 'cb',
    //     path: 'contentBlocks',
    //   },
    // ],
    limit: 999,
  };
  const qs = convertQueryCommandToQueryString(filters);
  return adminRequest.get<IContentBlockTemplates[]>(`/syndication-api/content_block_templates${qs}`);
};

/**
 * Generates Ad Preview <br>
 * API Endpoints:
 * <li>`/syndication-api/ad_preview/${feedKey}/${buildingId}` <br> via {@link adminRequest.post}</li>
 * <li>`/syndication-api/ad_preview/${feedKey}/${buildingId}` <br> via {@link adminRequest.get}</li>
 * @param {string} feedKey - key of the feed
 * @param {number} buildingId - id of the building
 * @param {any} [data] - optional data for preview, if no data fetches the preview, otherwise posts it
 */
export const generateAdPreview = (
  feedKey: string,
  buildingId: number,
  data?: any,
) => {
  if (data?.contentBlocks && data?.contentBlocks.length > 0) {
    return adminRequest.post(`/syndication-api/ad_preview/${feedKey}/${buildingId}`, data);
  }
  return adminRequest.get(`/syndication-api/ad_preview/${feedKey}/${buildingId}`);
};
/**
 * Fetches Auto On Settings <br>
 * API Endpoint - `/syndication-api/rentsync_settings${auto_on_filters}` <br> via {@link adminRequest.get}
 */
export const getAutoOnSettings = () => {
  const filters: IQueryCommandOptions = {
    where: [
      {
        field: 'field_key',
        operator: '',
        value: 'auto_on',
        condition: '',
        notOperator: false,
      },
    ],
    relations: [
      {
        path: 'partner',
        alias: 'p',
      },
      {
        path: 'p.partnerRentsyncRestrictions',
        alias: 'prr',
      },
      {
        path: 'p.partnerRentsyncSettings',
        alias: 'prs',
      },
    ],
    limit: 999,
  };
  const qs = convertQueryCommandToQueryString(filters);
  return adminRequest.get(`/syndication-api/rentsync_settings${qs}`);
};

/**
 * Fetches Zumper Brokerage Default Setting <br>
 * API Endpoint - `/syndication-api/rentsync_settings${auto_on_filters}` <br> via {@link adminRequest.get}
 */
export const getZumperBrokerageDefault = () => {
  const filters: IQueryCommandOptions = {
    where: [
      {
        field: 'field_key',
        operator: '',
        value: 'zumper_brokerage_default',
        condition: '',
        notOperator: false,
      },
    ],
    limit: 1,
  };
  const qs = convertQueryCommandToQueryString(filters);
  return adminRequest.get(`/syndication-api/rentsync_settings${qs}`);
};

/**
 * Fetches the account-wide "Disable 'Exclude Unit from Listings' for all properties" setting.
 * Scoped to the current client by the gateway, as with the other client-level lookups here.
 * API Endpoint - `/syndication-api/rentsync_settings${filters}` <br> via {@link adminRequest.get}
 */
export const getDisableUnitExclusions = () => {
  const filters: IQueryCommandOptions = {
    where: [
      {
        field: 'field_key',
        operator: '',
        value: 'disable_unit_exclusions',
        condition: '',
        notOperator: false,
      },
    ],
    limit: 1,
  };
  const qs = convertQueryCommandToQueryString(filters);
  return adminRequest.get<IRentsyncSettings[]>(`/syndication-api/rentsync_settings${qs}`);
};

/**
 * Fetches content blocks by rentsync setting ID
 * API Endpoint - '/syndication-api/content_blocks' via {@link adminRequest.get}
 * @param {number} rentsyncSettingId - id of the rentsync setting
 */
export const getContentBlocksBySettingId = (rentsyncSettingId: number) => {
  const filters: IQueryCommandOptions = {
    where: [
      {
        field: 'rentsyncSettingId',
        operator: 'like',
        value: rentsyncSettingId.toString(),
        condition: '',
        notOperator: false,
      },
    ],
    limit: 1,
  };
  const qs = convertQueryCommandToQueryString(filters);
  return adminRequest.get(`${contentBlocksPrefix}${qs}`);
};

export const saveContentBlocks = async (contentBlocks: IContentBlocks[]) => {
  const processor = new DataModifyProcessor<IContentBlocks>(contentBlocksUrl);
  processor.putItems = contentBlocks.filter((cb) => cb.id);
  processor.postItems = contentBlocks.filter((cb) => !cb.id);
  return processor.execute();
};

export default {
  get,
  getById,
  insert,
  update,
  updateContentBlocks,
  removeContentBlocks,
  remove,
  getContentBlockTemplates,
  generateAdPreview,
  getAutoOnSettings,
  getZumperBrokerageDefault,
  getDisableUnitExclusions,
  saveContentBlocks,
  getContentBlocksBySettingId,
};
